import { createEntityAdapter, nanoid } from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";
import { format } from "date-fns";
import { userRoles } from "../../enums/userRoles";
import { buildQueryParams } from "../../utils/commonMethods";

const incidentAdapter = createEntityAdapter({
  selectId: (incident) => incident.id,
});

const incidentGroupsAdapter = createEntityAdapter({
  selectId: (incidentGroup) => incidentGroup.id,
});

const incidentFormsAdapter = createEntityAdapter({
  selectId: (incidentForm) => incidentForm.id,
});

const incidentClosureFormsAdapter = createEntityAdapter({
  selectId: (incidentForm) => incidentForm.id,
});

const incidentDetailsAdapter = createEntityAdapter({
  selectId: (incidentDetail) => incidentDetail.id,
});

const incidentCreationDetailsAdapter = createEntityAdapter({
  selectId: (incidentCreationForm) => incidentCreationForm.id,
});

const incidentStatusAdapter = createEntityAdapter({
  selectId: (status) => status.id,
});

const incidentInsightsAdapter = createEntityAdapter({
  selectId: (insight) => insight.id,
});
const linkedIncidentsAdapter = createEntityAdapter({
  selectId: (incident) => incident.id,
});
const relatedIncidentsAdapter = createEntityAdapter({
  selectId: (incident) => incident.id,
});

const incidentsInitialState = incidentAdapter.getInitialState();
const incidentGroupsInitialState = incidentGroupsAdapter.getInitialState();
const incidentDetailsInitialState = incidentDetailsAdapter.getInitialState();
const incidentCreationDetailsInitialState =
  incidentCreationDetailsAdapter.getInitialState();
const incidentStatusInitialState = incidentStatusAdapter.getInitialState();
const incidentInsightsInitialState = incidentInsightsAdapter.getInitialState();
const incidentFormsInitialState = incidentFormsAdapter.getInitialState();
const incidentClosureFormsInitialState =
  incidentClosureFormsAdapter.getInitialState();
const linkedIncidentsInitialState = linkedIncidentsAdapter.getInitialState();
const relatedIncidentsInitialState = relatedIncidentsAdapter.getInitialState();

export const incidentSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getIncidents: builder.query({
      query: ({
        role,
        page,
        statusId,
        query,
        typeId,
        subTypeId,
        groupId,
        insightsStatus,
        departmentId,
        subDepartmentId,
        areaId,
        costCenterId,
        createdById,
        localMangerId,
        seniorMangerId,
        incidentAdvisorId,
        incidentOrderBy,
        orderByType,
      }) => {
        const params = {
          Page: page,
          StatusId: statusId,
          Query: query,
          TypeId: typeId,
          SubTypeId: subTypeId,
          GroupId: groupId,
          InsightsStatus: insightsStatus,
          IncidentOrderBy: incidentOrderBy,
          OrderByType: orderByType,
          departmentId,
          subDepartmentId,
          areaId,
          costCenterId,
          createdById,
          localMangerId,
          seniorMangerId,
          incidentAdvisorId,
        };

        const queryString = buildQueryParams(params);

        return `/incidents${
          role.includes(userRoles.ADMIN) ? "" : "/my"
        }?${queryString}`;
      },
      transformResponse: (responseData) => {
        const loadedIncidents = [].concat(
          ...(responseData.items || responseData).map((item) => {
            const {
              id,
              guid,
              name,
              createdOn,
              status,
              costCenter,
              updatedOn,
              type,
              subType,
              doesContainsLinkedIncidents,
            } = item;
            return {
              id,
              guid,
              name,
              location: costCenter ? costCenter?.description : "-",
              status,
              createdOn: createdOn
                ? format(createdOn, "dd/MM/yyyy hh:MM:ss")
                : "-",
              updatedOn: updatedOn
                ? format(updatedOn, "dd/MM/yyyy hh:MM:ss")
                : "-",
              type: type.description,
              subType: subType.description,
              doesContainsLinkedIncidents,
              pageSettings: {
                pageCount: responseData?.totalPages,
                pageSize: responseData?.pageSize,
                totalRecordsCount: responseData?.total,
                currentPage: responseData?.page,
              },
            };
          })
        );

        return incidentAdapter.setAll(incidentsInitialState, loadedIncidents);
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "Incidents", id: "ERROR" }];
        }
        return [
          { type: "Incidents", id: "LIST" },
          ...(result?.ids || []).map((id) => ({ type: "Incidents", id })),
        ];
      },
    }),
    getIncidentGroups: builder.query({
      query: () => `/incidents/groups`,
      transformResponse: (responseData) => {
        const loadedIncidentGroups = responseData;
        return incidentGroupsAdapter.setAll(
          incidentGroupsInitialState,
          loadedIncidentGroups
        );
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "IncidentGroups", id: "ERROR" }];
        }
        return [
          { type: "IncidentGroups", id: "LIST" },
          ...(result?.ids || []).map((id) => ({ type: "IncidentGroups", id })),
        ];
      },
    }),
    getLinkedIncidents: builder.query({
      query: (id) => `/incidents/${id}/linked-incidents`,
      transformResponse: (responseData) => {
        const loadedLinkedIncidents = responseData;
        return linkedIncidentsAdapter.setAll(
          linkedIncidentsInitialState,
          loadedLinkedIncidents
        );
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "LinkedIncidents", id: "ERROR" }];
        }
        return [
          { type: "LinkedIncidents", id: "LIST" },
          ...(result?.ids || []).map((id) => ({ type: "LinkedIncidents", id })),
        ];
      },
    }),
    getRelatedIncidents: builder.query({
      query: (id) => `/incidents/${id}/related-incidents`,
      transformResponse: (responseData) => {
        const loadedRelatedIncidents = responseData;
        return relatedIncidentsAdapter.setAll(
          relatedIncidentsInitialState,
          loadedRelatedIncidents
        );
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "RelatedIncidents", id: "ERROR" }];
        }
        return [
          { type: "RelatedIncidents", id: "LIST" },
          ...(result?.ids || []).map((id) => ({
            type: "RelatedIncidents",
            id,
          })),
        ];
      },
    }),

    getIncidentStatus: builder.query({
      query: () => `/incidents/statuses`,
      transformResponse: (responseData) => {
        const loadedIncidentStatus = responseData;
        return incidentStatusAdapter.setAll(
          incidentStatusInitialState,
          loadedIncidentStatus
        );
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "IncidentStatus", id: "ERROR" }];
        }
        return [
          { type: "IncidentStatus", id: "LIST" },
          ...(result?.ids || []).map((id) => ({ type: "IncidentStatus", id })),
        ];
      },
    }),
    getIncidentDetails: builder.query({
      query: (id) => `/incidents/${id}`,
      transformResponse: (responseData) => {
        const loadedIncidentDeails = responseData;
        return incidentDetailsAdapter.upsertOne(
          incidentDetailsInitialState,
          loadedIncidentDeails
        );
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "IncidentDetails", id: "ERROR" }];
        }
        return [{ type: "IncidentDetails", id: "LIST" }];
      },
    }),
    getIncidentForms: builder.query({
      query: (id) => `/incidents/${id}/forms`,
      transformResponse: (responseData) => {
        const loadedIncidentForms = responseData;
        return incidentFormsAdapter.setAll(
          incidentFormsInitialState,
          loadedIncidentForms
        );
      },
      providesTags: (result, error, arg) => {
        if (error) {
          return [{ type: "IncidentForms", id: "ERROR" }];
        }
        return [
          { type: "IncidentForms", id: "LIST" },
          ...(result?.ids || []).map((id) => ({ type: "IncidentForms", id })),
        ];
      },
    }),
    getIncidentClosureForms: builder.query({
      query: (id) => `/incidents/${id}/closure`,
      transformResponse: (responseData) => {
        const loadedIncidentClosureForms = {
          id: nanoid(),
          ...responseData,
        };
        return incidentClosureFormsAdapter.upsertOne(
          incidentClosureFormsInitialState,
          loadedIncidentClosureForms
        );
      },
      providesTags: (result, error, arg) => [
        { type: "IncidentClosureForms", id: arg },
      ],
    }),
    getIncidentCreationDetails: builder.query({
      query: ({ typeId, subTypeId }) => {
        if (typeId === 0 || subTypeId === 0) {
          return null;
        }
        return `/incidents/${typeId}/${subTypeId}/creation-details`;
      },
      transformResponse: (responseData) => {
        const loadedIncidentCreationDeails = {
          id: nanoid(),
          ...responseData,
        };
        return incidentCreationDetailsAdapter.upsertOne(
          incidentCreationDetailsInitialState,
          loadedIncidentCreationDeails
        );
      },
      providesTags: (result, error, arg) => [
        { type: "IncidentCreationDetails", id: result.id },
      ],
    }),
    getIncidentInsights: builder.query({
      query: () => `/insights/incidents`,
      transformResponse: (responseData) => {
        const loadedIncidentInsights = {
          ...responseData,
          id: nanoid(),
        };
        return incidentInsightsAdapter.upsertOne(
          incidentInsightsInitialState,
          loadedIncidentInsights
        );
      },
      providesTags: (result, error, arg) => [
        { type: "IncidentInsights", id: result?.id },
      ],
    }),

    createIncident: builder.mutation({
      query: (data) => ({
        url: `/incidents`,
        method: "POST",
        body: {
          ...data,
        },
      }),
      invalidatesTags: [
        { type: "Incidents", id: "LIST" },
        { type: "RelatedIncidents", id: "LIST" },
        { type: "IncidentInsights" },
      ],
    }),
    addTaskToIncident: builder.mutation({
      query: (data) => ({
        url: `/incidents/${data.id}/tasks`,
        method: "POST",
        body: {
          ...data.formData,
        },
      }),
      invalidatesTags: [
        { type: "Incidents", id: "LIST" },
        { type: "RelatedIncidents", id: "LIST" },
        { type: "Tasks", id: "LIST" },
        { type: "IncidentDetails" },
        { type: "IncidentInsights" },
      ],
    }),

    updateIncidentForm: builder.mutation({
      query: (data) => ({
        url: `/incidents/${data.incidentId}/forms/${data.formVersionId}`,
        method: "PUT",
        body: {
          data: data.formData,
        },
      }),
      onQueryStarted: async (data, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(
            apiSlice.util.invalidateTags([
              { type: "Incidents", id: "LIST" },
              { type: "RelatedIncidents", id: "LIST" },
              { type: "IncidentForms", id: "LIST" },
              { type: "IncidentDetails" },
            ])
          );
        } catch (err) {
          console.error("Update failed", err);
        }
      },
    }),

    initiateClosure: builder.mutation({
      query: ({ id, formsData }) => ({
        url: `/incidents/${id}:initiate-closure`,
        method: "PUT",
        body: {
          formsData,
        },
      }),
      invalidatesTags: [
        { type: "Incidents", id: "LIST" },
        { type: "IncidentDetails" },
        { type: "IncidentForms", id: "LIST" },
        { type: "IncidentInsights" },
      ],
    }),
    updateIncident: builder.mutation({
      query: ({ guid, data }) => ({
        url: `/incidents/${guid}`,
        method: "PUT",
        body: {
          ...data,
        },
      }),
      invalidatesTags: [
        { type: "Incidents", id: "LIST" },
        { type: "IncidentDetails" },
        { type: "IncidentForms", id: "LIST" },
        { type: "IncidentInsights" },
      ],
    }),
    closeIncident: builder.mutation({
      query: (id) => ({
        url: `/incidents/${id}:close`,
        method: "PUT",
        body: {},
      }),
      invalidatesTags: [
        { type: "Incidents", id: "LIST" },
        { type: "IncidentDetails" },
        { type: "IncidentForms", id: "LIST" },
        { type: "IncidentInsights" },
      ],
    }),
    completeIncident: builder.mutation({
      query: (data) => ({
        url: `/incidents/${data.incidentId}:complete`,
        method: "PUT",
        body: {
          formsData: data.formData,
        },
      }),
      invalidatesTags: [
        { type: "Incidents", id: "LIST" },
        { type: "RelatedIncidents", id: "LIST" },
        { type: "IncidentForms", id: "LIST" },
        { type: "IncidentDetails" },
        { type: "IncidentInsights" },
      ],
    }),
    attachDocument: builder.mutation({
      query: (formData) => ({
        url: `/attachment-documents`,
        method: "POST",
        body: formData,
        headers: {
          accept: "text/plain",
        },
      }),
    }),

    downloadAttachFile: builder.mutation({
      query: ({ id, filename }) => ({
        url: `/attachment-documents/${id}`,
        responseHandler: async (response) => {
          const blob = await response.blob();

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", filename || `attachment-${id}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        },
      }),
      keepUnusedDataFor: 0,
    }),

    linkDocumentToIncident: builder.mutation({
      query: ({ id, attachmentDocumentId }) => ({
        url: `/incidents/${id}/linked-documents`,
        method: "POST",
        body: {
          attachmentDocumentId,
        },
      }),
      invalidatesTags: [
        { type: "Incidents", id: "LIST" },
        { type: "IncidentDetails" },
      ],
    }),
    deleteAttachedDocument: builder.mutation({
      query: (id) => ({
        url: `/attachment-documents/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "Incidents", id: "LIST" },
        { type: "TaskDetails", id: "LIST" },
        { type: "IncidentDetails" },
      ],
    }),
    exportForms: builder.mutation({
      query: ({ incidentId, type }) => ({
        url: `/incidents/${incidentId}/export/json?Type=${type}`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetIncidentsQuery,
  useGetIncidentDetailsQuery,
  useGetIncidentCreationDetailsQuery,
  useGetIncidentStatusQuery,
  useCreateIncidentMutation,
  useGetIncidentInsightsQuery,
  useGetIncidentGroupsQuery,
  useGetIncidentFormsQuery,
  useGetIncidentClosureFormsQuery,
  useExportFormsMutation,
  useGetLinkedIncidentsQuery,
  useGetRelatedIncidentsQuery,
  useCompleteIncidentMutation,
  useUpdateIncidentFormMutation,
  useAddTaskToIncidentMutation,
  useAttachDocumentMutation,
  useUpdateIncidentMutation,
  useDeleteAttachedDocumentMutation,
  useLinkDocumentToIncidentMutation,
  useInitiateClosureMutation,
  useCloseIncidentMutation,
  useDownloadAttachFileMutation,
} = incidentSlice;
